// Disable HMR for middleware to prevent Nuxt virtual module conflicts
if (import.meta.hot) {
  import.meta.hot.decline()
}

// Navigation configuration

const NAVIGATION_CONFIG = {
  '/audience': {
    title: 'Audience',
    links: [
      ['Contacts', '/audience'],
      ['My tags', '/audience/tags'],
      ['Saved filters', '/audience/filters'],
    ],
  },
  '/campaigns': {
    title: 'Data capture',
    // listingPaths: directory/index pages for this section.
    // Clicking a filter item from outside these pages navigates here first.
    // The type filter store value is only synced on these pages to avoid
    // interfering with navigation to detail pages.
    listingPaths: ['/campaigns'],
    filterModule: 'campaignModule',
    filterStateKey: 'campaignsTypeFilter',
    filterMutation: 'campaignModule/SET_CAMPAIGNS_TYPE_FILTER',
    filterValues: ['all', 'presale', 'signup', 'waitlist', 'competition'],
    filters: [
      ['All data capture', 'all'],
      ['Presale registrations', 'presale'],
      ['Sign up form', 'signup'],
      ['Waitlist', 'waitlist'],
      ['Competition', 'competition'],
    ],
    links: [],
  },
  '/events': {
    title: 'Events',
    listingPaths: ['/events', '/events/past'],
    filterModule: 'eventModule',
    filterStateKey: 'eventsTypeFilter',
    filterMutation: 'eventModule/SET_EVENTS_TYPE_FILTER',
    filterValues: ['all', 'events', 'groups'],
    filters: [
      ['All events', 'all'],
      ['Events', 'events'],
      ['Groups', 'groups'],
    ],
    links: [],
  },
}

export default defineNuxtRouteMiddleware ((to, _from) => {
  const nuxtApp = useNuxtApp()
  const store = nuxtApp.$store

  if (!store) {
    console.warn('Store not available in middleware')
    return
  }

  // Find matching section for the current route
  const sectionPath = Object.keys(NAVIGATION_CONFIG).find(path =>
    to.path.startsWith(path),
  )

  if (sectionPath) {
    // Enable secondary nav for this section
    store.commit('secondaryNavigationModule/SET_IS_SECONDARY_NAVIGATION_ENABLED', true)

    // Initialise type filter from URL query — only on listing pages to avoid
    // triggering the listing's type watcher while navigating to detail pages
    const sectionConfig = NAVIGATION_CONFIG[sectionPath]
    const listingPaths = sectionConfig?.listingPaths ?? [sectionPath]
    if (sectionConfig?.filters && sectionConfig?.filterMutation && listingPaths.includes(to.path)) {
      const validValues = sectionConfig.filterValues ?? []
      const urlType = to.query.type
      if (urlType && validValues.includes(urlType)) {
        store.commit(sectionConfig.filterMutation, urlType)
      }
      else if (!urlType) {
        store.commit(sectionConfig.filterMutation, 'all')
      }
    }

    // Setup the navigation with the proper title and items
    setupNavigation(store, sectionPath, nuxtApp.$router)

    // First check localStorage for persisted preference
    let savedVisibility
    try {
      if (typeof window !== 'undefined' && window.localStorage) { // Only run in browser
        const savedValue = localStorage.getItem('secondaryNavVisible')
        if (savedValue !== null) {
          savedVisibility = JSON.parse(savedValue)
        }
      }
    }
    catch (e) {
      console.warn('Failed to read secondaryNavVisible from localStorage:', e)
    }

    const currentVisibility = store.getters['secondaryNavigationModule/isSecondaryNavigationVisible']

    // If we have a saved preference in localStorage, use it (only if different from current)
    if (typeof savedVisibility === 'boolean' && savedVisibility !== currentVisibility) {
      store.commit('secondaryNavigationModule/SET_IS_SECONDARY_NAVIGATION_VISIBLE', savedVisibility)
    }
    // Otherwise, if visibility is undefined in the store, default to true
    else if (currentVisibility === undefined) {
      store.commit('secondaryNavigationModule/SET_IS_SECONDARY_NAVIGATION_VISIBLE', true)
    }
    // Otherwise keep whatever is in the store (preserving user preference)
  }
  else {
    // Only disable secondary nav when navigating away from supported sections
    store.commit('secondaryNavigationModule/SET_IS_SECONDARY_NAVIGATION_ENABLED', false)
  }
})

/**
 * Set up secondary navigation based on route section
 * @param {object} store - Vuex store
 * @param {string} sectionPath - The section path key from config
 */
function setupNavigation(store, sectionPath, router) {
  if (!store) return

  const sectionConfig = NAVIGATION_CONFIG[sectionPath]
  if (!sectionConfig) return

  store.commit('secondaryNavigationModule/SET_IS_SECONDARY_NAVIGATION_ENABLED', true)
  store.commit('secondaryNavigationModule/SET_NAVIGATION_TITLE', sectionConfig.title)

  const listingPaths = sectionConfig.listingPaths ?? [sectionPath]

  const filterItems = (sectionConfig.filters || []).map(([label, filterValue]) => ({
    label,
    filterValue,
    // Carried through to the secondary nav so it can resolve the active item
    // against the right store module (events, campaigns, …).
    filterModule: sectionConfig.filterModule,
    filterStateKey: sectionConfig.filterStateKey,
    action: () => {
      if (sectionConfig.filterMutation) {
        store.commit(sectionConfig.filterMutation, filterValue)
      }
      // Navigate to the primary listing path when not already on a listing page,
      // passing the filter as a query param so the middleware doesn't reset it on arrival
      if (!listingPaths.includes(router.currentRoute.value.path)) {
        navigateTo({ path: sectionPath, query: filterValue !== 'all' ? { type: filterValue } : {} })
      }
    },
  }))

  const linkItems = (sectionConfig.links || []).map(([label, url]) => ({
    label,
    action: () => {
      // Navigate without changing secondary navigation visibility
      navigateTo(url)
    },
  }))

  const items = [...filterItems, ...linkItems]

  const navigationItems = [
    {
      title: null,
      items,
    },
  ]

  store.commit('secondaryNavigationModule/SET_NAVIGATION_ITEMS', navigationItems)
}
